import React from 'react';
import { Campaign } from '../../types/Campaign';
import { CalendarIcon, DollarSignIcon, TagIcon, PercentIcon, ArrowRightIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="overflow-x-auto" data-id="element-359">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-360">
        <thead className="bg-gray-50" data-id="element-361">
          <tr data-id="element-362">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-363">
              Campaign
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-364">
              Code
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-365">
              Discount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-366">
              Budget Usage
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-367">
              Expiry
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-368">
              Status
            </th>
            <th scope="col" className="relative px-6 py-3" data-id="element-369">
              <span className="sr-only" data-id="element-370">View</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-371">
          {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onCampaignClick(campaign.id)} data-id="element-372">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-373">
                  <div className="flex items-center" data-id="element-374">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-375">
                      <TagIcon size={18} className="text-blue-600" data-id="element-376" />
                    </div>
                    <div className="ml-4" data-id="element-377">
                      <div className="text-sm font-medium text-gray-900" data-id="element-378">
                        {campaign.title}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-379">
                        {campaign.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-380">
                  <div className="text-sm font-medium text-gray-900" data-id="element-381">
                    {campaign.code}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-382">
                    {campaign.codeStrategy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-383">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-384">
                    {campaign.discountPercentage > 0 ? <>
                        <PercentIcon size={16} className="mr-1 text-purple-500" data-id="element-385" />
                        {campaign.discountPercentage}%
                      </> : <>
                        <DollarSignIcon size={16} className="mr-1 text-green-500" data-id="element-386" />
                        {campaign.fixedDiscountAmount} {campaign.currency}
                      </>}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-387">
                    {campaign.discountCarriedBy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-388">
                  <div className="flex items-center" data-id="element-389">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-390">
                      <div className="bg-blue-600 h-2.5 rounded-full" style={{
                  width: `${calculateBudgetUsage(campaign)}%`
                }} data-id="element-391"></div>
                    </div>
                    <span className="text-sm text-gray-500" data-id="element-392">
                      {calculateBudgetUsage(campaign)}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-393">
                    {campaign.budgetUsed.toLocaleString()} /{' '}
                    {campaign.budget.toLocaleString()} {campaign.currency}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-394">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-395">
                    <CalendarIcon size={16} className="mr-1 text-gray-500" data-id="element-396" />
                    {campaign.expiryDate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-397">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(campaign.status)}`} data-id="element-398">
                    {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-399">
                  <button className="text-blue-600 hover:text-blue-900" data-id="element-400">
                    <ArrowRightIcon size={16} data-id="element-401" />
                  </button>
                </td>
              </tr>) : <tr data-id="element-402">
              <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-403">
                No campaigns found
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
}